// Ops periods and Manila dates, shared by Reports, Catch-weight and Payouts (docs/PHASE7-CONTRACT.md).
// Days are Manila calendar days 'YYYY-MM-DD', from/to inclusive. Pure, no Intl (same rule as the
// shared core), so node --test can check it.
import { manilaDay } from '@popcart/core/v1';

/** @typedef {{ from: string; to: string }} Period */

const DAY_MS = 86_400_000;
const MANILA_MS = 8 * 3_600_000;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The API's cap on a report or catch-weight period. */
export const MAX_PERIOD_DAYS = 92;

/** @param {string} day @returns {boolean} */
export function isDay(day) {
  if (typeof day !== 'string' || !DAY_RE.test(day)) return false;
  const t = Date.parse(`${day}T00:00:00Z`);
  return Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === day;
}

/** The Manila day `n` days after `day` (negative goes back). @param {string} day @param {number} n */
export function addDays(day, n) {
  return new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);
}

/** Days in from..to inclusive (0 when to is before from). @param {Period} p */
export function dayCount({ from, to }) {
  const n = Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS) + 1;
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

/** Today in Manila. @param {Date | number} [now] */
export function manilaToday(now = Date.now()) {
  return manilaDay(new Date(now));
}

/** The last `n` Manila days, today included. @param {number} n @param {Date | number} [now] @returns {Period} */
export function lastNDays(n, now = Date.now()) {
  const to = manilaToday(now);
  return { from: addDays(to, -(Math.max(1, n) - 1)), to };
}

/** Last week, Monday to Sunday, in Manila (the payouts default). @param {Date | number} [now] @returns {Period} */
export function lastWeek(now = Date.now()) {
  const today = manilaToday(now);
  const sinceMonday = (new Date(`${today}T00:00:00Z`).getUTCDay() + 6) % 7;
  const monday = addDays(today, -sinceMonday);
  return { from: addDays(monday, -7), to: addDays(monday, -1) };
}

/** This week so far, Monday to today, in Manila. @param {Date | number} [now] @returns {Period} */
export function thisWeek(now = Date.now()) {
  const today = manilaToday(now);
  const sinceMonday = (new Date(`${today}T00:00:00Z`).getUTCDay() + 6) % 7;
  return { from: addDays(today, -sinceMonday), to: today };
}

/** @param {Period | null | undefined} a @param {Period | null | undefined} b */
export function samePeriod(a, b) {
  return !!a && !!b && a.from === b.from && a.to === b.to;
}

/**
 * Check a custom period before it goes to the API.
 * @param {{ from: string; to: string }} p
 * @param {{ max?: number; today?: string }} [opts]
 * @returns {{ ok: true; value: Period } | { ok: false; error: string; field: 'from' | 'to' }}
 */
export function validatePeriod(p, { max = MAX_PERIOD_DAYS, today } = {}) {
  const from = String(p.from ?? '').trim();
  const to = String(p.to ?? '').trim();
  if (!isDay(from)) return { ok: false, field: 'from', error: 'Pick a start date.' };
  if (!isDay(to)) return { ok: false, field: 'to', error: 'Pick an end date.' };
  if (to < from) return { ok: false, field: 'to', error: 'The end date comes before the start.' };
  if (today && to > today) return { ok: false, field: 'to', error: 'The period can’t end after today.' };
  if (dayCount({ from, to }) > max) return { ok: false, field: 'from', error: `Up to ${max} days at a time.` };
  return { ok: true, value: { from, to } };
}

/** "Mon 6 Oct". @param {string} day */
export function dayLabel(day) {
  if (!isDay(day)) return '—';
  const d = new Date(`${day}T00:00:00Z`);
  return `${DOW[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** "6 Oct". @param {string} day */
export function shortDay(day) {
  if (!isDay(day)) return '—';
  const d = new Date(`${day}T00:00:00Z`);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** "29 Sep – 5 Oct 2026", "6 Oct 2026" for one day. @param {Period} p */
export function periodLabel({ from, to }) {
  if (!isDay(from) || !isDay(to)) return '—';
  const year = to.slice(0, 4);
  if (from === to) return `${shortDay(to)} ${year}`;
  const left = from.slice(0, 4) === year ? shortDay(from) : `${shortDay(from)} ${from.slice(0, 4)}`;
  return `${left} – ${shortDay(to)} ${year}`;
}

/** The Manila wall clock of an instant. @param {string | null | undefined} iso */
function manilaParts(iso) {
  const t = typeof iso === 'string' ? Date.parse(iso) : Number.NaN;
  if (!Number.isFinite(t)) return null;
  const d = new Date(t + MANILA_MS);
  return { day: d.getUTCDate(), month: MONTHS[d.getUTCMonth()], hh: d.getUTCHours(), mm: String(d.getUTCMinutes()).padStart(2, '0') };
}

/** "6 Oct, 14:05" in Manila. @param {string | null | undefined} iso */
export function manilaStamp(iso) {
  const p = manilaParts(iso);
  return p ? `${p.day} ${p.month}, ${p.hh}:${p.mm}` : '—';
}

/** "6 Oct" in Manila. @param {string | null | undefined} iso */
export function manilaDate(iso) {
  const p = manilaParts(iso);
  return p ? `${p.day} ${p.month}` : '—';
}
